import { useState, useRef, useEffect } from "react";
import { Modal, Form, Button } from "react-bootstrap";

interface CatalogItem {
  id: number;
  name: string;
}

interface ClinicalHistoryCatalogProps {
  show: boolean;
  onHide: () => void;
  title: string;
  searchPlaceholder: string;
  selectLabel: string;
  items: CatalogItem[];
  onSearch: (searchValue: string) => Promise<CatalogItem[]>;
  onGetAll: () => Promise<CatalogItem[]>;
  selectedItemId: number | null;
  onSelectChange: (itemId: number | null) => void;
  onSubmit: () => Promise<void>;
  isSubmitting: boolean;
  submitButtonText?: string;
  debounceDelay?: number;
}

const ClinicalHistoryCatalog = ({
  show,
  onHide,
  title,
  searchPlaceholder,
  selectLabel,
  items,
  onSearch,
  onGetAll,
  selectedItemId,
  onSelectChange,
  onSubmit,
  isSubmitting,
  submitButtonText = "Add",
  debounceDelay = 750,
}: ClinicalHistoryCatalogProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [catalogItems, setCatalogItems] = useState<CatalogItem[]>(items);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Update catalog items when items prop changes
  useEffect(() => {
    setCatalogItems(items);
  }, [items]);

  // Load all items when modal opens
  useEffect(() => {
    if (show) {
      setSearchTerm("");
      onGetAll().then((result) => {
        if (result) {
          setCatalogItems(result);
        }
      });
    }
  }, [show, onGetAll]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleClose = () => {
    setSearchTerm("");
    onSelectChange(null);
    // Clear any pending search timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    onHide();
  };

  const handleSearch = (searchValue: string) => {
    setSearchTerm(searchValue);

    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set a new timeout to debounce the API call
    searchTimeoutRef.current = setTimeout(() => {
      if (searchValue.trim()) {
        onSearch(searchValue).then((result) => {
          if (result) {
            setCatalogItems(result);
          }
        });
      } else {
        onGetAll().then((result) => {
          if (result) {
            setCatalogItems(result);
          }
        });
      }
      searchTimeoutRef.current = null;
    }, debounceDelay);
  };

  const handleSubmit = async () => {
    if (!selectedItemId) return;
    await onSubmit();
    handleClose();
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Search {selectLabel}</Form.Label>
            <Form.Control
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Select {selectLabel}</Form.Label>
            <Form.Select
              value={selectedItemId || ""}
              onChange={(e) =>
                onSelectChange(
                  e.target.value ? parseInt(e.target.value) : null
                )
              }
            >
              <option value="">Choose {selectLabel.toLowerCase()}...</option>
              {catalogItems.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!selectedItemId || isSubmitting}
        >
          {isSubmitting ? "Adding..." : submitButtonText}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ClinicalHistoryCatalog;

